<script setup lang="ts">
import { computed } from "vue";
import { products, formatPrice } from "../data/products";
import ProductArt from "./ProductArt.vue";

const emit = defineEmits<{
  (e: "open", slug: string): void;
  (e: "open-install"): void;
}>();

const featured = computed(() => products.find((p) => p.hero) ?? products[0]);
</script>

<template>
  <section class="hero">
    <div class="hero__bg" aria-hidden="true">
      <div class="hero__grain" />
    </div>

    <div class="os1-shell hero__shell">
      <div class="hero__copy">
        <span class="os1-eyebrow">Volume 01 / Warm Red</span>
        <h1 class="hero__title">
          Wear the operating <em>system</em>.
        </h1>
        <p class="hero__lead">
          OS1 is a small, quiet clothing house. Linen, wool, merino and silk in the
          range of warm reds we keep coming back to. Made in small runs, finished by
          hand, designed to be worn until the seams know your shoulders.
        </p>
        <div class="hero__featured">
          <span class="os1-tag">Featured</span>
          <div class="hero__featured-line">
            <span class="hero__featured-name">{{ featured.name }}</span>
            <span class="hero__featured-price">{{ formatPrice(featured.price) }}</span>
          </div>
          <p class="hero__featured-blurb">{{ featured.subtitle }}</p>
        </div>
        <div class="hero__actions">
          <button class="os1-button os1-button--solid" @click="emit('open', featured.slug)">
            Shop the look
          </button>
          <button class="os1-button os1-button--ghost" @click="emit('open-install')">
            Install OS1 →
          </button>
        </div>
        <div class="hero__meta">
          <div>
            <span class="hero__meta-label">Shipping</span>
            <span>Free over $180</span>
          </div>
          <div>
            <span class="hero__meta-label">Returns</span>
            <span>30 days, no notes needed</span>
          </div>
          <div>
            <span class="hero__meta-label">Companion</span>
            <span>Desktop and APK</span>
          </div>
        </div>
      </div>
      <div class="hero__visual">
        <button class="hero__product" @click="emit('open', featured.slug)" aria-label="Open featured product">
          <ProductArt :product="featured" aspect="3 / 4" />
          <span class="hero__product-tag">View</span>
        </button>
        <div class="hero__stack" aria-hidden="true">
          <div class="hero__stack-card" :style="{ background: 'linear-gradient(135deg,#4a0d0d,#2a0606)' }" />
          <div class="hero__stack-card" :style="{ background: 'linear-gradient(135deg,#ff4632,#7a1a14)' }" />
        </div>
      </div>
    </div>

    <div class="hero__strip" aria-hidden="true">
      <span>OS1 — A QUIET WARDROBE</span>
      <span>·</span>
      <span>BUILT FOR THE PERSON ON THE OTHER END OF THE LINE</span>
      <span>·</span>
      <span>VOLUME 01 — WARM RED</span>
      <span>·</span>
      <span>SHIPS WORLDWIDE</span>
      <span>·</span>
      <span>HAND-FINISHED</span>
    </div>
  </section>
</template>

<style scoped>
.hero {
  position: relative;
  padding-top: clamp(2.5rem, 6vw, 5rem);
  padding-bottom: clamp(2rem, 5vw, 4rem);
  overflow: hidden;
}

.hero__bg {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse at 80% 10%, rgba(255, 70, 50, 0.22), transparent 55%),
    radial-gradient(ellipse at 10% 90%, rgba(180, 50, 40, 0.18), transparent 60%),
    linear-gradient(180deg, #150505 0%, #1c0707 60%, #100303 100%);
  z-index: -1;
}

.hero__grain {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(rgba(255, 200, 180, 0.04) 1px, transparent 1px);
  background-size: 3px 3px;
  mix-blend-mode: overlay;
  opacity: 0.5;
}

.hero__shell {
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: clamp(1.5rem, 4vw, 3.4rem);
  align-items: center;
}

.hero__title {
  font-size: var(--os1-step-5);
  line-height: 0.95;
  color: var(--os1-paper);
  margin: 1.1rem 0 1.2rem;
  max-width: 14ch;
}

.hero__title em {
  font-style: italic;
  color: var(--os1-coral);
}

.hero__lead {
  font-size: var(--os1-step-2);
  color: var(--os1-ink-soft);
  max-width: 46ch;
}

.hero__featured {
  margin-top: 1.8rem;
  padding: 1rem 1.1rem;
  border: 1px solid var(--os1-line);
  border-radius: var(--os1-radius);
  background: rgba(20, 5, 5, 0.55);
}

.hero__featured-line {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 1rem;
  margin: 0.6rem 0 0.2rem;
}

.hero__featured-name {
  font-family: var(--os1-font-serif);
  font-size: 1.3rem;
  color: var(--os1-paper);
}

.hero__featured-price {
  font-family: var(--os1-font-mono);
  color: var(--os1-rose);
  font-size: 0.95rem;
}

.hero__featured-blurb {
  color: var(--os1-ink-mute);
  font-size: 0.85rem;
}

.hero__actions {
  display: flex;
  gap: 0.7rem;
  margin-top: 1.4rem;
  flex-wrap: wrap;
}

.hero__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 1.4rem;
  margin-top: 2rem;
  color: var(--os1-ink-soft);
  font-size: 0.78rem;
}

.hero__meta-label {
  display: block;
  font-size: 0.6rem;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: var(--os1-ink-mute);
  margin-bottom: 0.15rem;
}

.hero__visual {
  position: relative;
  isolation: isolate;
}

.hero__product {
  position: relative;
  display: block;
  width: 100%;
  border-radius: var(--os1-radius);
  overflow: hidden;
  box-shadow: var(--os1-shadow);
  cursor: pointer;
}

.hero__product-tag {
  position: absolute;
  right: 1rem;
  bottom: 1rem;
  padding: 0.5rem 0.9rem;
  background: rgba(8, 2, 2, 0.7);
  backdrop-filter: blur(8px);
  border: 1px solid var(--os1-line-strong);
  color: var(--os1-paper);
  font-size: 0.65rem;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  border-radius: 999px;
}

.hero__stack {
  position: absolute;
  inset: 0;
  z-index: -1;
}

.hero__stack-card {
  position: absolute;
  width: 60%;
  aspect-ratio: 3 / 4;
  border-radius: var(--os1-radius);
  opacity: 0.55;
}

.hero__stack-card:nth-child(1) {
  top: -2rem;
  right: -2.5rem;
  transform: rotate(6deg);
}

.hero__stack-card:nth-child(2) {
  bottom: -3rem;
  left: -3rem;
  transform: rotate(-8deg);
  opacity: 0.35;
}

.hero__strip {
  margin-top: clamp(2rem, 4vw, 3rem);
  border-top: 1px solid var(--os1-line);
  border-bottom: 1px solid var(--os1-line);
  padding-block: 0.8rem;
  display: flex;
  gap: 2rem;
  justify-content: center;
  align-items: center;
  color: var(--os1-ink-mute);
  font-size: 0.66rem;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
}

@media (max-width: 900px) {
  .hero__shell {
    grid-template-columns: 1fr;
  }
  .hero__visual {
    order: -1;
    max-width: 480px;
    margin-inline: auto;
  }
  .hero__strip {
    flex-wrap: nowrap;
    overflow-x: auto;
  }
}
</style>
